import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Search, Plus, Check, X, LayoutGrid, List as ListIcon, ChevronDown,
  Laptop, Monitor, Server, Network, Shield, HardDrive, Printer, Headphones,
  AppWindow, Video, Sparkles,
} from 'lucide-react';
import {
  CatalogSearchParams,
  CatalogSearchHit,
  CatalogSearchRecommendation,
  SeedSku,
  getDistributorSeedSkus,
  listEnrichments,
  recommendDistributorCatalogSearch,
  saveDistributorSeedSkus,
  searchDistributorCatalog,
} from '../../services/distributorService';
import { ProductDetailPane } from './ProductDetailPane';

/**
 * Browse a distributor's live catalogue. Search by keyword, narrow with
 * removable filter chips (vendor / category / in-stock), pick a sort,
 * see live price + stock alongside each result, and click "+ Add" to
 * append a SKU to the seed list.
 *
 * Self-contained — maintains its own snapshot of the seed list to render
 * "Already added" pills, and pulls a one-shot count of Active enrichments
 * for the "Showing N of M live SKUs" denominator. Sibling SeedSkuPanel
 * does not auto-refresh after an add (deferred to v1.5).
 */

interface CatalogBrowsePanelProps {
  distributorId: string;
  distributorLabel: string;
  /**
   * Callback to navigate the parent IngramTab to the Enrichment review
   * sub-tab. Optional `sku` deep-links the review panel's master/detail to
   * that specific SKU (and switches its filter to wherever the SKU lives).
   */
  onSwitchToReview?: (sku?: string) => void;
}

type ViewMode = 'list' | 'grid';
type SortKey = 'relevance' | 'priceAsc' | 'priceDesc' | 'stockDesc';

const SORT_LABELS: Record<SortKey, string> = {
  relevance: 'Relevance',
  priceAsc: 'Price (low → high)',
  priceDesc: 'Price (high → low)',
  stockDesc: 'Stock (high → low)',
};

/**
 * Quick-start prompts. These no longer map directly to Ingram categories:
 * the backend planner decides whether a category is safe or whether vendor
 * and keyword filters are more reliable for the user's intent.
 */
const CATEGORY_TILES: Array<{ label: string; prompt: string; Icon: React.ComponentType<{ className?: string }>; tone: string }> = [
  { label: 'Notebooks',   prompt: 'business notebook with 16GB RAM and 512GB SSD in stock', Icon: Laptop,    tone: 'bg-blue-50 text-blue-700' },
  { label: 'Desktops',    prompt: 'business desktop with 16GB RAM and 512GB SSD',            Icon: Monitor,   tone: 'bg-cyan-50 text-cyan-700' },
  { label: 'Servers',     prompt: 'server with redundant power and warranty',                Icon: Server,    tone: 'bg-violet-50 text-violet-700' },
  { label: 'Networking',  prompt: 'network switch or wireless access point in stock',        Icon: Network,   tone: 'bg-emerald-50 text-emerald-700' },
  { label: 'Security',    prompt: 'firewall appliance in stock',                             Icon: Shield,    tone: 'bg-rose-50 text-rose-700' },
  { label: 'Storage',     prompt: 'NAS or storage drive in stock',                           Icon: HardDrive, tone: 'bg-amber-50 text-amber-700' },
  { label: 'Displays',    prompt: 'business monitor 27 inch in stock',                       Icon: Monitor,   tone: 'bg-indigo-50 text-indigo-700' },
  { label: 'Peripherals', prompt: 'keyboard mouse headset webcam dock',                      Icon: Headphones,tone: 'bg-fuchsia-50 text-fuchsia-700' },
  { label: 'Print',       prompt: 'business printer or multifunction scanner',               Icon: Printer,   tone: 'bg-slate-50 text-slate-700' },
  { label: 'Software',    prompt: 'software licence subscription',                           Icon: AppWindow, tone: 'bg-orange-50 text-orange-700' },
  { label: 'AV',          prompt: 'video conferencing audio visual hardware',                 Icon: Video,     tone: 'bg-teal-50 text-teal-700' },
];

export function CatalogBrowsePanel({
  distributorId,
  distributorLabel,
  onSwitchToReview,
}: CatalogBrowsePanelProps) {
  const [keyword, setKeyword] = useState('');
  // Filter chips, applied client-side for vendor + category, server-side for inStock.
  const [vendorFilter, setVendorFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sort, setSort] = useState<SortKey>('relevance');
  const [view, setView] = useState<ViewMode>('list');
  const [results, setResults] = useState<CatalogSearchHit[] | null>(null);
  const [recommendation, setRecommendation] = useState<CatalogSearchRecommendation | null>(null);
  const [plannedParams, setPlannedParams] = useState<CatalogSearchParams | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seedSkus, setSeedSkus] = useState<SeedSku[]>([]);
  const [adding, setAdding] = useState<string | null>(null);
  const [recentlyAdded, setRecentlyAdded] = useState<Set<string>>(new Set());
  const [activeCount, setActiveCount] = useState<number | null>(null);
  const [enrichedSkus, setEnrichedSkus] = useState<Set<string>>(new Set());
  // Index into sortedResults for the open detail pane. null = no pane open.
  const [paneIndex, setPaneIndex] = useState<number | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch seed list + active count once on mount. The active count drives
  // the "Showing N of M live SKUs" denominator and lags slightly behind
  // changes — acceptable for a denominator readout.
  const reloadContext = useCallback(async () => {
    try {
      const [cfg, list] = await Promise.all([
        getDistributorSeedSkus(distributorId).catch(() => null),
        listEnrichments(distributorId).catch(() => null),
      ]);
      if (cfg) setSeedSkus(cfg.skus ?? []);
      if (list) {
        const items = list.items ?? [];
        setActiveCount(items.filter((item) => item.status === 'Active').length);
        setEnrichedSkus(new Set(items.map((item) => item.sku)));
      }
    } catch {
      // Non-fatal — page still usable without these denominators.
    }
  }, [distributorId]);

  useEffect(() => {
    reloadContext();
  }, [reloadContext]);

  const seedSkuSet = useMemo(
    () => new Set(seedSkus.map((s) => s.sku)),
    [seedSkus],
  );

  const executeSearch = async (params: CatalogSearchParams) => {
    setLoading(true);
    setError(null);
    try {
      const result = await searchDistributorCatalog(distributorId, {
        ...params,
        limit: 50,
      });
      setResults(result.items);
      setPlannedParams(params);
    } catch (err: any) {
      setError(err.message ?? 'Search failed');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const intent = keyword.trim();
    if (!intent) {
      if (e) setError('Describe what you want to search for.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const nextRecommendation = await recommendDistributorCatalogSearch(
        distributorId,
        intent,
        { inStock: inStockOnly || undefined, limit: 50 },
      );
      setRecommendation(nextRecommendation);

      const nextParams = paramsFromRecommendation(nextRecommendation, intent);
      setVendorFilter(nextParams.vendor ?? null);
      setCategoryFilter(nextParams.category ?? null);
      setInStockOnly(Boolean(nextParams.inStock));

      const result = await searchDistributorCatalog(distributorId, nextParams);
      setResults(result.items);
      setPlannedParams(nextParams);
    } catch (err: any) {
      setError(err.message ?? 'Search failed');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = () => {
    setKeyword('');
    setVendorFilter(null);
    setCategoryFilter(null);
    setInStockOnly(false);
    setRecommendation(null);
    setPlannedParams(null);
    setResults(null);
    setError(null);
    searchInputRef.current?.focus();
  };

  const applyFilterOverride = (next: Partial<CatalogSearchParams>) => {
    if (!plannedParams) {
      if ('vendor' in next || 'vendors' in next) setVendorFilter(next.vendor ?? null);
      if ('category' in next) setCategoryFilter(next.category ?? null);
      if ('inStock' in next) setInStockOnly(Boolean(next.inStock));
      return;
    }
    const base: CatalogSearchParams = plannedParams ?? {
      keyword: keyword.trim() || undefined,
      limit: 50,
    };
    const merged = { ...base, ...next, limit: 50 };
    setVendorFilter(merged.vendor ?? null);
    setCategoryFilter(merged.category ?? null);
    setInStockOnly(Boolean(merged.inStock));
    executeSearch(merged);
  };

  const handleAdd = async (hit: CatalogSearchHit) => {
    setAdding(hit.sku);
    setError(null);
    try {
      const next: SeedSku[] = [
        ...seedSkus,
        {
          sku: hit.sku,
          category: hit.category?.l1 ?? null,
          name: hit.description ?? hit.vendorPartNumber ?? hit.sku,
          enabled: true,
        },
      ];
      await saveDistributorSeedSkus(distributorId, next);
      setSeedSkus(next);
      setRecentlyAdded((prev) => new Set(prev).add(hit.sku));
    } catch (err: any) {
      setError(err.message ?? 'Failed to add SKU');
    } finally {
      setAdding(null);
    }
  };

  const isAdded = (sku: string) => seedSkuSet.has(sku) || recentlyAdded.has(sku);

  // Client-side sort (the upstream search has no sort param).
  const sortedResults = useMemo(() => {
    if (!results) return null;
    const arr = [...results];
    switch (sort) {
      case 'priceAsc':
        arr.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
        break;
      case 'priceDesc':
        arr.sort((a, b) => (b.price ?? -1) - (a.price ?? -1));
        break;
      case 'stockDesc':
        arr.sort((a, b) => (b.stock ?? -1) - (a.stock ?? -1));
        break;
      // 'relevance' = whatever Ingram returned, untouched.
    }
    return arr;
  }, [results, sort]);

  const hasResults = sortedResults !== null && sortedResults.length > 0;
  const matchCount = sortedResults?.length ?? 0;

  return (
    <div className="px-6 pt-2 pb-10">
      {/* In-tab header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-[18px] font-semibold text-slate-900 tracking-tight">
            Add products from {distributorLabel}
          </h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            Find a product to add to your catalogue. We'll enrich it with AI before publishing
            {activeCount !== null && ` · ${activeCount.toLocaleString()} live SKUs available`}.
          </p>
        </div>
        <div className="inline-flex items-center rounded-md border border-slate-200 p-0.5 bg-white">
          <button
            type="button"
            onClick={() => setView('grid')}
            disabled={view === 'grid'}
            title="Grid view (coming soon)"
            className={`inline-flex p-1.5 rounded ${
              view === 'grid' ? 'bg-orange-50 text-orange-600' : 'text-slate-400 hover:text-slate-700'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setView('list')}
            className={`inline-flex p-1.5 rounded ${
              view === 'list' ? 'bg-orange-50 text-orange-600' : 'text-slate-400 hover:text-slate-700'
            }`}
          >
            <ListIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <form
        onSubmit={handleSearch}
        className="flex flex-wrap items-center gap-2 mb-3"
      >
        <div className="relative flex-1 min-w-[320px]">
          <Sparkles className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-orange-500" />
          <input
            ref={searchInputRef}
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="What are you searching for?"
            disabled={loading}
            autoFocus
            className="w-full rounded-lg border-2 border-orange-300 pl-10 pr-44 py-3 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
          />
          {sortedResults !== null && (
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-500">
              {matchCount} match{matchCount === 1 ? '' : 'es'} in catalogue
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => applyFilterOverride({ inStock: !inStockOnly || undefined })}
          disabled={loading}
          className={`inline-flex text-xs px-2.5 py-2 rounded-md border ${
            inStockOnly
              ? 'border-orange-300 bg-orange-50 text-orange-700'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
          }`}
        >
          {inStockOnly ? '✓ ' : '+ '}In stock only
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-medium px-4 py-2.5 transition-colors"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </button>
      </form>

      {(vendorFilter || categoryFilter || inStockOnly) && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {vendorFilter && (
            <FilterChip
              label={`Vendor: ${vendorFilter}`}
              onClear={() => applyFilterOverride({ vendor: undefined, vendors: undefined })}
            />
          )}
          {categoryFilter && (
            <FilterChip
              label={`Category: ${categoryFilter}`}
              onClear={() => applyFilterOverride({ category: undefined })}
            />
          )}
          {inStockOnly && (
            <FilterChip
              label="In stock"
              onClear={() => applyFilterOverride({ inStock: undefined })}
            />
          )}
        </div>
      )}

      {recommendation && (
        <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50/60 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-orange-900">
            <span className="font-semibold">Recommended Ingram search</span>
            {recommendation.search.keywords.map((value) => (
              <span key={`kw-${value}`} className="rounded bg-white/80 border border-orange-200 px-2 py-0.5">
                keyword: {value}
              </span>
            ))}
            {recommendation.search.vendors.map((value) => (
              <span key={`vendor-${value}`} className="rounded bg-white/80 border border-orange-200 px-2 py-0.5">
                vendor: {value}
              </span>
            ))}
            {recommendation.search.vendorPartNumbers.map((value) => (
              <span key={`vpn-${value}`} className="rounded bg-white/80 border border-orange-200 px-2 py-0.5">
                VPN: {value}
              </span>
            ))}
            {recommendation.search.category && (
              <span className="rounded bg-white/80 border border-orange-200 px-2 py-0.5">
                category: {recommendation.search.category}
              </span>
            )}
            {recommendation.search.inStock && (
              <span className="rounded bg-white/80 border border-orange-200 px-2 py-0.5">
                in stock
              </span>
            )}
          </div>
          {recommendation.search.rationale && (
            <p className="mt-1.5 text-xs text-orange-800">
              {recommendation.search.rationale}
            </p>
          )}
          {recommendation.warnings.length > 0 && (
            <p className="mt-1 text-xs text-orange-700">
              {recommendation.warnings[0]}
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Result-count + sort row */}
      {sortedResults !== null && (
        <div className="flex items-center justify-between mb-2 px-1">
          <p className="text-xs text-slate-500">
            {hasResults ? (
              <>
                Showing <span className="font-semibold text-slate-900">{matchCount}</span>
                {activeCount !== null && (
                  <>
                    {' of '}
                    <span className="font-semibold text-slate-900">{activeCount.toLocaleString()}</span>
                  </>
                )}
                {' live SKUs'}
              </>
            ) : (
              <>No matches.{' '}<button onClick={handleClearAll} className="inline-flex underline hover:text-slate-700">Clear search</button></>
            )}
          </p>
          {hasResults && (
            <div className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              Sort:
              <div className="relative">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  className="appearance-none bg-transparent pr-5 font-medium text-slate-900 focus:outline-none cursor-pointer"
                >
                  {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                    <option key={k} value={k}>{SORT_LABELS[k]}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-500 pointer-events-none" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results table */}
      {loading && (
        <div className="text-sm text-slate-500 inline-flex items-center gap-2 px-1 py-3">
          <Loader2 className="h-4 w-4 animate-spin" />
          Searching {distributorLabel}…
        </div>
      )}

      {!loading && hasResults && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[11px] text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium py-3 px-4 w-28">SKU</th>
                <th className="text-left font-medium py-3 px-4">Description</th>
                <th className="text-left font-medium py-3 px-4 w-36">Vendor</th>
                <th className="text-left font-medium py-3 px-4 w-32">Category</th>
                <th className="text-right font-medium py-3 px-4 w-28">Price</th>
                <th className="text-right font-medium py-3 px-4 w-20">Stock</th>
                <th className="text-right font-medium py-3 px-4 w-28">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedResults!.map((hit, hitIdx) => {
                const added = isAdded(hit.sku);
                const busy = adding === hit.sku;
                return (
                  <tr
                    key={hit.sku}
                    onClick={() => setPaneIndex(hitIdx)}
                    className="hover:bg-slate-50/60 cursor-pointer"
                  >
                    <td className="py-3 px-4 font-mono text-sm tabular-nums">
                      <SkuToken sku={hit.sku} />
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-slate-900">
                        {prettifyDescription(hit.description, hit.vendor)}
                      </div>
                      {hit.vendorPartNumber && (
                        <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                          {hit.vendorPartNumber}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          applyFilterOverride({
                            vendor: hit.vendor || undefined,
                            vendors: hit.vendor ? [hit.vendor] : undefined,
                          });
                        }}
                        title={`Filter by ${hit.vendor ?? 'vendor'}`}
                        className="inline-flex items-center gap-2"
                      >
                        <VendorBadge vendor={hit.vendor} />
                        <span className="text-sm text-slate-700">{hit.vendor ?? '—'}</span>
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">
                      {hit.category?.l1 ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            applyFilterOverride({ category: hit.category!.l1 || undefined });
                          }}
                          className="inline-flex hover:text-slate-900"
                          title={`Filter by ${hit.category.l1}`}
                        >
                          {hit.category.l1}
                        </button>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-900 text-right tabular-nums">
                      {formatPrice(hit.price)}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700 text-right tabular-nums">
                      {hit.stock !== undefined && hit.stock !== null ? (
                        <span className={hit.stock > 0 ? '' : 'text-slate-400'}>
                          {hit.stock.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {added ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                          <Check className="h-3.5 w-3.5" />
                          Added
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleAdd(hit); }}
                          disabled={busy}
                          className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 border border-slate-300 hover:border-slate-400 hover:bg-slate-50 rounded-md px-2.5 py-1"
                        >
                          {busy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Plus className="h-3.5 w-3.5" />
                          )}
                          Add
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && sortedResults === null && !error && (
        <div className="mt-4">
          <div className="text-sm text-slate-700 font-medium mb-1">
            Quick starts
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Add a vendor, model family, specs, budget, stock preference, or an exact part number.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
            {CATEGORY_TILES.map(({ label, prompt, Icon, tone }) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  setKeyword(prompt);
                  searchInputRef.current?.focus();
                }}
                className="inline-flex flex-col items-start gap-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm p-3 text-left transition"
              >
                <span className={`inline-flex items-center justify-center h-9 w-9 rounded-md ${tone}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium text-slate-900">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Slide-out detail pane */}
      {paneIndex !== null && sortedResults && sortedResults[paneIndex] && (
        <ProductDetailPane
          results={sortedResults}
          index={paneIndex}
          distributorId={distributorId}
          distributorLabel={distributorLabel}
          isInSeed={isAdded(sortedResults[paneIndex].sku)}
          hasKnownEnrichment={enrichedSkus.has(sortedResults[paneIndex].sku)}
          adding={adding === sortedResults[paneIndex].sku}
          onClose={() => setPaneIndex(null)}
          onPrev={() => setPaneIndex((i) => (i !== null && i > 0 ? i - 1 : i))}
          onNext={() => setPaneIndex((i) =>
            i !== null && sortedResults && i < sortedResults.length - 1 ? i + 1 : i,
          )}
          onAdd={(hit) => handleAdd(hit)}
          onEdit={(hit) => {
            // Jump to the Enrichment review sub-tab + deep-link the master/
            // detail to this specific SKU. Parent IngramTab pipes the sku
            // into EnrichmentReviewPanel's initialSelectedSku prop.
            onSwitchToReview?.(hit.sku);
            setPaneIndex(null);
          }}
        />
      )}
    </div>
  );
}

// ─── small components ────────────────────────────────────────────────

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-orange-200 bg-orange-50 text-orange-700 text-xs font-medium px-2.5 py-1.5">
      {label}
      <button
        type="button"
        onClick={onClear}
        className="inline-flex hover:text-orange-900"
        aria-label={`Remove ${label}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function VendorBadge({ vendor }: { vendor: string | null }) {
  const label = (vendor ?? '??').slice(0, 2).toUpperCase();
  // Map common vendors to brand-ish colours; fall back to a stable hash colour.
  const map: Record<string, string> = {
    LE: 'bg-red-500 text-white',
    HP: 'bg-blue-500 text-white',
    DE: 'bg-cyan-600 text-white', // Dell
    CI: 'bg-blue-700 text-white', // Cisco
    FO: 'bg-rose-600 text-white', // Fortinet
    MI: 'bg-emerald-600 text-white', // Microsoft
    AP: 'bg-slate-700 text-white', // Apple / APC
    AR: 'bg-orange-500 text-white', // Aruba
    LO: 'bg-violet-500 text-white', // Logitech
  };
  const colour = map[label] ?? 'bg-slate-500 text-white';
  return (
    <span className={`inline-flex items-center justify-center h-6 w-6 rounded text-[10px] font-bold ${colour}`}>
      {label}
    </span>
  );
}

function SkuToken({ sku }: { sku: string }) {
  // Highlight the leading 3 chars in orange to make scanning easier — matches
  // the way Ingram SKUs cluster by family prefix in their numbering scheme.
  const head = sku.slice(0, 3);
  const tail = sku.slice(3);
  return (
    <>
      <span className="text-orange-600">{head}</span>
      <span className="text-slate-700">{tail}</span>
    </>
  );
}

// ─── helpers ────────────────────────────────────────────────

function paramsFromRecommendation(
  recommendation: CatalogSearchRecommendation,
  fallbackIntent: string,
): CatalogSearchParams {
  const search = recommendation.search;
  const keywords = cleanList(search.keywords);
  const vendors = cleanList(search.vendors);
  const vendorPartNumbers = cleanList(search.vendorPartNumbers);

  return {
    keyword: keywords.length === 0 && vendorPartNumbers.length === 0 ? fallbackIntent : undefined,
    keywords: keywords.length > 0 ? keywords : undefined,
    vendor: vendors[0] || undefined,
    vendorPartNumbers: vendorPartNumbers.length > 0 ? vendorPartNumbers : undefined,
    category: search.category || undefined,
    type: search.type || undefined,
    inStock: search.inStock || undefined,
    includeDiscontinued: search.includeDiscontinued || undefined,
    limit: search.limit || 50,
  };
}

function cleanList(values: string[] | undefined): string[] {
  return (values ?? []).map((value) => value.trim()).filter(Boolean);
}

function formatPrice(price: number | null | undefined): React.ReactNode {
  if (price === null || price === undefined) return <span className="text-slate-400">—</span>;
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function prettifyDescription(description: string | null, vendor: string | null): string {
  if (!description) return '—';
  // Ingram descriptions often start with the vendor brand (e.g. "LENOVO
  // THINKPAD T16 GEN 4..."). Strip that leading vendor token since we
  // already render the vendor in its own column.
  let out = description;
  if (vendor) {
    const re = new RegExp(`^${vendor}\\s+`, 'i');
    out = out.replace(re, '');
  }
  return out;
}
